import { useState, useEffect, FC, memo } from 'react';
import './App.css';
import axios from "axios";
import i18n from "i18next";
import { useTranslation, initReactI18next } from "react-i18next";
import translationJA from './locales/ja.json';
import translationEN from './locales/en.json';

i18n
    .use(initReactI18next) // passes i18n down to react-i18next
    .init({
        // the translations
        // (tip move them in a JSON file and import them,
        // or even better, manage them via a UI: https://react.i18next.com/guides/multiple-translation-files#manage-your-translations-with-a-management-gui)
        resources: {
            ja: { translation: translationJA },
            en: { translation: translationEN },
        },
        lng: "en", // if you're using a language detector, do not define the lng option
        fallbackLng: "en",

        interpolation: {
            escapeValue: false // react already safes from xss => https://www.i18next.com/translation-function/interpolation#unescape
        }
    });

type Watch = {
    id: string,
    name: string,
    price_prefix?: string,
    price: number,
    price_suffix?: string,
    image_name?: string,
    image_large: string,
    image_thumb: string,
    alt: string,
    link: string
}
type Band = {
    id: string,
    name: string,
    color: string,
    price_prefix?: string,
    price: number,
    price_suffix?: string,
    image_name?: string,
    image_large: string,
    image_thumb: string,
    alt: string,
    link: string
}
type Props = {
    lang?: string | null,
}
export const App: FC<Props> = memo(({ lang }) => {
    const { t, i18n } = useTranslation();
    const [currentWatchClass] = useState<string>('ph_head current');
    const [currentBandClass] = useState<string>('ph_band current absolute');

    const [watchList, setWatchList] = useState<Watch[]>([]);
    const [bandList, setBandList] = useState<Band[]>([]);
    const [bandDefault, setBandDefault] = useState<Band>();
    const [currentWatch, setCurrentWatch] = useState<Watch>();
    const [currentBand, setCurrentBand] = useState<Band>();

    const [downloadImage, setDownloadImage] = useState<string>('');

    const changeWatch = (watch: Watch) => watch === currentWatch ? setCurrentWatch(watchList[0]) : setCurrentWatch(watch);

    const changeBand = (band: Band) => band === currentBand ? setCurrentBand(bandDefault) : setCurrentBand(band);

    useEffect(() => {
        if (currentWatch && currentBand) {
            const watchName = currentWatch.image_name || currentWatch.name;
            const bandName = currentBand.image_name || currentBand.name;
            setDownloadImage(`assets/image/img_${watchName}_${bandName}.jpg`);
        } else {
            setDownloadImage('');
        }
    }, [currentWatch, currentBand]);


    const reset = () => {
        // console.log('reset trigger');
        changeWatch(watchList[0]);
        if (bandDefault) {
            changeBand(bandDefault);
        }
    };

    //言語設定
    useEffect(() => {
        if (lang) {
            i18n.changeLanguage(lang);
        }
    }, [lang, i18n]);

    //マスタデータ取得
    useEffect(() => {
        // console.log('master init trigger');
        if (lang) {
            axios.get(`assets/products.json`).then((res) => {
                setWatchList(res.data.watch);
                setBandList(res.data.band);
                setBandDefault(res.data.defaultBand);
            });
        }
    }, [lang]);

    //Watch初期選択
    useEffect(() => {
        // console.log('watch init trigger');
        setCurrentWatch(watchList[0]);
    }, [watchList]);

    //Band初期選択
    useEffect(() => {
        // console.log('band init trigger');
        setCurrentBand(bandDefault);
    }, [bandDefault]);


    return (
        <>
            <div className="preload">
                {watchList.map((watch) => (
                    <link key={watch.id} rel="preload" href={watch.image_large} as="image" />
                ))}
                {bandList.map((band) => (
                    <link key={band.id} rel="preload" href={band.image_large} as="image" />
                ))}
            </div>
            <div className="fix_area">
                <div className="inner">
                    <div className="ph">
                        <div className={currentWatchClass}>
                            {currentWatch && <img src={currentWatch.image_large} alt="currentWatch.alt" />}
                        </div>
                        <div className={currentBandClass}>
                            {currentBand && <img src={currentBand.image_large} alt="currentBand.alt" />}
                        </div>
                    </div>
                    <div className="text">
                        <div className="box_name cf">
                            <div>
                                <p className="h_name">{currentWatch && currentWatch.name}</p>
                                <p className="b_name">{currentBand && currentBand.name}</p>
                            </div>
                        </div>
                        <div className="btn">
                            <a href={downloadImage} target="_blank">{t('preview-image')}</a>
                        </div>
                    </div>
                </div>
            </div>
            <div className="select_area">
                <div className="inner">
                    <ul className="tab cf">
                        <li className="watch active"><span>Watch</span></li>
                        <li className="band"><span>Band</span></li>
                        <span className="box_reset">
                            <button type="button" onClick={reset}>Reset</button>
                        </span>
                    </ul>
                    <div className="tab_contents">
                        <ul className="show wrap_watch cf">
                            {watchList.map((watch, index) => (
                                <li key={watch.id} onClick={() => changeWatch(watch)} className={watch === currentWatch ? "active" : ""}>
                                    <div className="ph">
                                        <img src={watch.image_thumb} alt={watch.alt} />
                                    </div>
                                    <dl>
                                        <dt>{watch.name}</dt>
                                        <dd>
                                            {watch.price_prefix && <span>{watch.price_prefix} </span>}
                                            {watch.price.toLocaleString()}
                                            {watch.price_suffix && <span>{watch.price_suffix}</span>}
                                        </dd>
                                    </dl>
                                    {watch.link && <div className="btn">
                                        <a href={watch.link} target="_blank">{t('buy')}</a>
                                    </div>}
                                </li>
                            ))}
                            <li>
                                <div className="footer_sp"></div>
                            </li>
                        </ul>
                        <ul className="wrap_band cf">
                            {bandList.map((band, index) => (
                                <li key={band.id} onClick={() => changeBand(band)} className={band === currentBand ? "active" : ""}>
                                    <div className="ph">
                                        <img src={band.image_thumb} alt={band.alt} />
                                    </div>
                                    <dl>
                                        <dt>{band.name}</dt>
                                        <dt>{band.color}</dt>
                                        <dd>
                                            {band.price_prefix && <span>{band.price_prefix} </span>}
                                            {band.price.toLocaleString()}
                                            {band.price_suffix && <span>{band.price_suffix}</span>}
                                        </dd>
                                    </dl>
                                    {band.link && <div className="btn">
                                        <a href={band.link} target="_blank">{t('buy')}</a>
                                    </div>}
                                </li>
                            ))}
                            <li>
                                <div className="footer_sp"></div>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </>
    );
});
